import type {
    FC,
    InputHTMLAttributes,
    ReactNode,
    Ref
} from 'react'
import type { LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

interface AuthFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'placeholder'> {
    ref?: Ref<HTMLInputElement>
    id: string
    label: string
    icon: LucideIcon
    error?: string
    hint?: string
    trailing?: ReactNode
}

export const AuthField: FC<AuthFieldProps> = ({ ref, id, label, icon: Icon, error, hint, trailing, className, ...props }): ReactNode => {
    const messageId = error || hint ? `${id}-message` : undefined

    return (
        <div className={className}>
            <div className='group relative'>
                <Icon
                    aria-hidden='true'
                    className={cn(
                        'pointer-events-none absolute top-1/2 left-4 z-10 size-5 -translate-y-1/2 transition-colors duration-200',
                        error ? 'text-destructive' : 'text-ink/40 group-focus-within:text-brand-ink'
                    )}
                />

                <input
                    ref={ref}
                    id={id}
                    placeholder=' '
                    aria-invalid={error ? true : undefined}
                    aria-describedby={messageId}
                    className={cn(
                        'peer h-[62px] w-full rounded-[20px] border bg-[#fcfaf7] pt-5 pl-12 text-[16px] font-medium text-ink outline-none transition-[border-color,box-shadow,background-color] duration-200 autofill:shadow-[inset_0_0_0_1000px_#fcfaf7] focus:bg-white',
                        trailing ? 'pr-14' : 'pr-4',
                        error
                            ? 'border-destructive/70 focus:shadow-[0_0_0_4px_rgba(200,50,31,0.12)]'
                            : 'border-input hover:border-ink/25 focus:border-brand focus:shadow-[0_0_0_4px_rgba(242,107,58,0.16)]'
                    )}
                    {...props}
                />

                <label
                    htmlFor={id}
                    className='pointer-events-none absolute top-[19px] left-12 -translate-y-1/2 text-[12px] font-semibold text-muted-foreground transition-all duration-200 peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-[15px] peer-placeholder-shown:font-medium peer-focus:top-[19px] peer-focus:text-[12px] peer-focus:font-semibold peer-focus:text-brand-ink'
                >
                    {label}
                </label>

                {trailing && (
                    <div className='absolute top-1/2 right-2 -translate-y-1/2'>
                        {trailing}
                    </div>
                )}
            </div>

            {(error || hint) && (
                <p
                    id={messageId}
                    className={cn('mt-1.5 px-1 text-[13px] leading-snug', error ? 'animate-in font-medium text-destructive fade-in slide-in-from-top-1' : 'text-muted-foreground')}
                >
                    {error ?? hint}
                </p>
            )}
        </div>
    )
}
